import React, { useState, useEffect, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import api from "../utils/api";
import { classifyError, shouldShowError } from "../utils/errorClassifier";
import { EndpointErrorLabel } from "./ErrorComponents";

const ForecastingChart = ({ username, availableMachines = [] }) => {
  const [selectedMachine, setSelectedMachine] = useState("");
  const [forecastData, setForecastData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [forecastError, setForecastError] = useState(null);
  const [mapeData, setMapeData] = useState(null);
  const [mapeLoading, setMapeLoading] = useState(false);
  const [mapeError, setMapeError] = useState(null);

  // Only GRID machines
  const gridMachines = useMemo(() => {
    return availableMachines.filter(
      (m) => m.category && m.category.toUpperCase() === "GRID"
    );
  }, [availableMachines]);

  // Set default machine
  useEffect(() => {
    if (gridMachines.length > 0 && !selectedMachine) {
      setSelectedMachine(gridMachines[0].name);
    }
  }, [gridMachines, selectedMachine]);

  useEffect(() => {
    if (!selectedMachine || !username) return;

    const fetchForecast = async () => {
      setLoading(true);
      setForecastError(null);

      try {
        const response = await api.get("/get_forecasted_data", {
          params: {
            username: username,
            machine_name: selectedMachine,
          },
        });

        setForecastData(response.data);
        setForecastError(null);
      } catch (err) {
        console.error("Forecast fetch error:", err);
        const classifiedErr = classifyError(err, 'get_forecasted_data');
        if (classifiedErr.severity !== 'silent') {
          setForecastError(classifiedErr);
        }
        setForecastData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchForecast();
  }, [selectedMachine, username]);

  // Fetch MAPE accuracy data
  useEffect(() => {
    if (!selectedMachine || !username) return;

    const fetchMape = async () => {
      setMapeLoading(true);
      setMapeError(null);
      try {
        const response = await api.get("/mape", {
          params: {
            username: username,
            machine_name: selectedMachine,
          },
        });
        setMapeData(response.data);
        setMapeError(null);
      } catch (err) {
        console.error("MAPE fetch error:", err);
        const classifiedErr = classifyError(err, 'mape');
        if (classifiedErr.severity !== 'silent') {
          setMapeError(classifiedErr);
        } else {
          setMapeError(null);
        }
        setMapeData(null);
      } finally {
        setMapeLoading(false);
      }
    };

    fetchMape();
  }, [selectedMachine, username]);

  const chartOptions = useMemo(() => {
    return {
      chart: {
        type: "line",
        height: 450,
        zoom: { enabled: true },
        toolbar: { show: true },
      },
      colors: ["#3b82f6", "#10b981"],
      stroke: {
        width: [3, 3],
        curve: "smooth",
        dashArray: [0, 6],
      },
      title: {
        text: `Energy Forecasting: ${selectedMachine}`,
        align: "left",
      },
      xaxis: {
        type: "datetime",
        labels: { datetimeUTC: false },
      },
      yaxis: {
        title: { text: "Power (kW)" },
        labels: {
          formatter: (val) => val.toFixed(2),
        },
      },
      tooltip: {
        shared: true,
        x: { format: "dd MMM yyyy HH:mm" },
      },
      legend: {
        position: "top",
        horizontalAlign: "right",
      },
      markers: {
        size: 0,
        hover: { size: 5 },
      },
      grid: {
        borderColor: "#f1f5f9",
      },
    };
  }, [selectedMachine]);

  const series = useMemo(() => {
    if (!forecastData) return [];

    const parseTimestamp = (ts) => {
      if (!ts) return Date.now();
      // Replace first space with 'T' for safer cross-browser date parsing
      // Handles format like "2026-03-24 17:00:00" or "2026-03-25 16:00:00+05:00"
      const isoTs = ts.replace(" ", "T");
      return new Date(isoTs).getTime();
    };

    // Historical data
    const historicalSeries =
      forecastData.last_24_hours?.map((item) => ({
        x: parseTimestamp(item.timestamp),
        y: item.power_kw,
      })) || [];

    let predictionSeries = [];
    const predictions = forecastData.predictions?.next_4_hours;

    if (predictions) {
      // Preference: reference_timestamp_pak -> last historical point -> now
      let baseTimestamp;
      if (forecastData.reference_timestamp_pak) {
        baseTimestamp = parseTimestamp(forecastData.reference_timestamp_pak);
      } else if (historicalSeries.length > 0) {
        baseTimestamp = historicalSeries[historicalSeries.length - 1].x;
      } else {
        baseTimestamp = Date.now();
      }

      const hour = 3600000;

      predictionSeries = [
        {
          x: baseTimestamp + hour,
          y: predictions.next_1h,
        },
        {
          x: baseTimestamp + hour * 2,
          y: predictions.next_2h,
        },
        {
          x: baseTimestamp + hour * 3,
          y: predictions.next_3h,
        },
        {
          x: baseTimestamp + hour * 4,
          y: predictions.next_4h,
        },
      ];

      // Connect historical line to forecast line for a continuous visual
      if (historicalSeries.length > 0) {
        predictionSeries.unshift(
          historicalSeries[historicalSeries.length - 1]
        );
      }
    }

    const finalSeries = [
      {
        name: "Historical (Last 24h)",
        data: historicalSeries,
      },
    ];

    // Only add forecast series if there's actual prediction data
    if (predictionSeries.length > 1) {
      finalSeries.push({
        name: "Forecast (Next 4h)",
        data: predictionSeries,
      });
    }

    return finalSeries;
  }, [forecastData]);

  const noPrediction =
    forecastData?.predictions?.next_4_hours === null &&
    (forecastData?.predictions?.message ||
      forecastData?.predictions?.next_4_hours === null);

  return (
    <div
      style={{
        backgroundColor: "#fff",
        borderRadius: "10px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        padding: "20px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <h2 style={{ margin: 0 }}>Load Forecasting</h2>
          
          {/* MAPE display */}
          {mapeLoading ? (
            <span style={{ fontSize: "14px", color: "#64748b" }}>Calculating Accuracy...</span>
          ) : mapeData ? (
            <div 
              style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: "8px",
                background: mapeData.valid ? "#f0fdf4" : "#fffbeb",
                padding: "4px 12px",
                borderRadius: "20px",
                border: `1px solid ${mapeData.valid ? "#bbf7d0" : "#fef3c7"}`,
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
              }}
              title={mapeData.valid ? "Forecast accuracy based on historical performance" : "Model is currently calibrating. Accuracy value may be less reliable."}
            >
              <span style={{ fontSize: "13px", fontWeight: 600, color: mapeData.valid ? "#166534" : "#92400e" }}>
                Accuracy (MAPE): {mapeData.mape_percent?.toFixed(2)}%
              </span>
              {!mapeData.valid && (
                <span 
                  style={{ 
                    fontSize: "10px", 
                    background: "#f59e0b", 
                    color: "white", 
                    padding: "1px 6px", 
                    borderRadius: "10px",
                    fontWeight: 700,
                    textTransform: "uppercase"
                  }}
                >
                  Calibrating
                </span>
              )}
            </div>
          ) : null}
        </div>

        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <label style={{ fontWeight: 600 }}>Select Machine:</label>

          <select
            value={selectedMachine}
            onChange={(e) => setSelectedMachine(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: "1px solid #d1d5db",
              minWidth: "200px",
              cursor: "pointer",
            }}
          >
            {gridMachines.map((m) => (
              <option key={m.name} value={m.name}>
                {m.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div
          style={{
            height: "450px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          Loading forecasting data...
        </div>
      ) : forecastError && shouldShowError(forecastError) ? (
        <EndpointErrorLabel error={forecastError} />
      ) : forecastData ? (
        <>
          {noPrediction && (
            <div
              style={{
                background: "#f0f9ff",
                padding: "16px",
                borderRadius: "12px",
                marginBottom: "20px",
                color: "#0369a1",
                fontSize: "14px",
                border: "1px solid #bae6fd",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <span style={{ fontSize: "18px" }}>ℹ️</span>
              {forecastData.predictions.message ||
                "Forecasting model is currently being trained. Check back soon."}
            </div>
          )}

          <ReactApexChart
            options={chartOptions}
            series={series}
            type="line"
            height={450}
          />
        </>
      ) : (
        <div
          style={{
            height: "450px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          Select a machine to view forecasting
        </div>
      )}
    </div>
  );
};

export default ForecastingChart;